import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithCustomToken,
  GoogleAuthProvider,
  fetchSignInMethodsForEmail,
} from "firebase/auth";
import {
  doc,
  setDoc,
  getDoc,
  getDocs,
  where,
  collection,
  query,
  updateDoc,
} from "firebase/firestore";
import { auth, db, realtimeDb } from "../firebaseFunctions/config";
import { ref, set, get, remove } from "firebase/database";
const provider = new GoogleAuthProvider();

// Function to generate a unique referral code in PEB##### format
// Uses timestamp + random to ensure uniqueness without querying all users
const generateReferralCode = async (businessName) => {
  // Use last 3 digits of timestamp + 4 random digits for uniqueness
  const timestamp = Date.now().toString().slice(-3);
  const randomNum = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
  const referralCode = `PEB${timestamp}${randomNum}`;
  return referralCode;
};

export const authService = {
  async checkEmailExists(email) {
    try {
      const methods = await fetchSignInMethodsForEmail(auth, email);
      return {
        exists: methods.length > 0,
        methods: methods
      };
    } catch (error) {
      // If error, assume email doesn't exist
      return { exists: false, methods: [] };
    }
  },

  async signInExistingUser(email, password) {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      return {
        success: true,
        user: result.user
      };
    } catch (error) {
      let message = 'Login failed. Please try again.';
      if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
        message = 'Incorrect password. Please try again.';
      } else if (error.code === 'auth/too-many-requests') {
        message = 'Too many attempts. Please try again later.';
      }
      return {
        success: false,
        message: message
      };
    }
  },

  async getUserData(uid) {
    try {
      const userDoc = await getDoc(doc(db, "users", uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        return {
          success: true,
          userType: userData.userType,
          isEoSignedUp: userData.isEoSignedUp || false,
          isMpsSignedUp: userData.isMpsSignedUp || false,
        };
      }
      return { success: false, message: "User data not found" };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },
  async signUp(email, password, userType) {
    try {
      let authUser;

      try {
        // 1. Try to create new user (auth-first pattern)
        const result = await createUserWithEmailAndPassword(auth, email, password);
        authUser = result.user;

        // Force token refresh to sync auth state with Firestore
        // This prevents "Missing or insufficient permission" errors on new user creation
        await authUser.getIdToken(true);
      } catch (createError) {
        if (createError.code === 'auth/email-already-in-use') {
          // 2. Account exists in Firebase Auth - try to sign in
          try {
            const result = await signInWithEmailAndPassword(auth, email, password);
            authUser = result.user;

            // Force token refresh to sync auth state with Firestore
            await authUser.getIdToken(true);

            // 3. Check existing profile by UID (allowed by Firestore rules)
            const userDoc = await getDoc(doc(db, "users", authUser.uid));
            if (userDoc.exists()) {
              const userData = userDoc.data();
              const hasThisProfile = userType === "EO" ? userData.eoProfile : userData.mpsProfile;
              const signInMethod = userType === "EO" ? userData.eoSignUpMethod : userData.mpsSignUpMethod;
              const isSignedUp = userType === "EO"
                ? (userData.isEoSignedUp || userData.eoProfile)
                : (userData.isMpsSignedUp || userData.mpsProfile);

              if (hasThisProfile) {
                // Already has this profile - check sign-in method
                if (signInMethod === "Google") {
                  await auth.signOut();
                  return {
                    success: false,
                    requiresGoogleSignIn: true,
                    message: "This account uses Google sign-in. Please use the Google button to continue."
                  };
                }
                // Auto-login existing email user
                await this.updateSignInMethod(authUser.uid, userType);
                return {
                  success: true,
                  user: authUser,
                  isSignedUp: isSignedUp,
                  message: "Welcome back! You've been logged in.",
                  autoLoggedIn: true
                };
              }
              // User exists but doesn't have this profile type - continue to create profile
            }
            // No user doc or no profile - continue to create profile
          } catch (signInError) {
            // Wrong password or other auth error
            if (signInError.code === 'auth/wrong-password' || signInError.code === 'auth/invalid-credential') {
              return {
                success: false,
                message: 'An account with this email already exists. Please use the correct password or sign in instead.',
              };
            }
            throw signInError;
          }
        } else {
          throw createError;
        }
      }

      // 4. Update or create user document
      await this.updateUserProfile(authUser.uid, email, userType, "Email", false);

      return {
        success: true,
        user: authUser,
        message: "Account created successfully!",
        emailVerificationSent: false
      };
    } catch (error) {
      return {
        success: false,
        message: error.message,
      };
    }
  },
  /**
   * @deprecated This function queries by email which requires collection-level read access.
   * Use checkExistingUserByUid() after authenticating instead.
   */
  async getUserSignUpStatus(email) {
    const usersRef = collection(db, "users");
    const q = query(usersRef, where("email", "==", email));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return { exists: false };
    }

    const userData = querySnapshot.docs[0].data();
    return {
      exists: true,
      isEoSignedUp: userData.isEoSignedUp || false,
      isMpsSignedUp: userData.isMpsSignedUp || false,
    };
  },

  async signIn(email, password, userType) {
    try {
      // 1. Sign in directly (auth-first pattern)
      const result = await signInWithEmailAndPassword(auth, email, password);

      // 2. Check profile by UID (allowed by Firestore rules)
      const userDoc = await getDoc(doc(db, "users", result.user.uid));

      if (!userDoc.exists()) {
        await auth.signOut();
        return {
          success: false,
          message: "No account found. Please sign up first.",
        };
      }

      const userData = userDoc.data();
      const hasProfile = userType === "EO" ? userData.eoProfile : userData.mpsProfile;

      if (!hasProfile) {
        await auth.signOut();
        return {
          success: false,
          message: `No ${userType} account found with this email`,
        };
      }

      const signInMethod = userType === "EO" ? userData.eoSignUpMethod : userData.mpsSignUpMethod;
      if (signInMethod === "Google") {
        await auth.signOut();
        return {
          success: false,
          message: "Please sign in with Google",
        };
      }

      const isSignedUp = userType === "EO"
        ? (userData.isEoSignedUp || userData.eoProfile)
        : (userData.isMpsSignedUp || userData.mpsProfile);

      // 3. Update signedInAs in Firestore
      const userRef = doc(db, "users", result.user.uid);
      await setDoc(userRef, { signedInAs: userType, updatedAt: new Date() }, { merge: true });

      return {
        success: true,
        user: result.user,
        isSignedUp: isSignedUp || false,
      };
    } catch (error) {
      // Handle Firebase Auth errors with user-friendly messages
      let message = error.message;
      if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
        message = 'Incorrect password. Please try again.';
      } else if (error.code === 'auth/user-not-found') {
        message = 'No account found with this email. Please sign up first.';
      } else if (error.code === 'auth/too-many-requests') {
        message = 'Too many failed attempts. Please try again later.';
      }
      return {
        success: false,
        message: message,
      };
    }
  },

  /**
   * @deprecated This function queries by email which requires collection-level read access.
   * Use checkExistingUserByUid() after authenticating instead.
   */
  async checkExistingGoogleUser(email) {
    // eslint-disable-next-line no-useless-catch
    try {
      const usersRef = collection(db, "users");
      const q = query(usersRef, where("email", "==", email));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const userData = querySnapshot.docs[0].data();
        // Check if user has an unverified email account
        if (!userData.emailVerified) {
          await auth.signOut();
          throw new Error("Unverified email in existing account");
        }
        return userData;
      }
      return null;
    } catch (error) {
      throw error;
    }
  },

  async signInWithGoogleOnly() {
    try {
      provider.setCustomParameters({
        prompt: 'select_account'
      });

      const result = await signInWithPopup(auth, provider);
      const uid = result.user.uid;

      // Read user document directly by UID instead of querying by email
      const userDoc = await getDoc(doc(db, "users", uid));

      if (userDoc.exists()) {
        const userData = userDoc.data();
        const userType = userData.signedInAs || userData.userType;

        if (userType) {
          return {
            success: true,
            user: result.user,
            isNewUser: false,
            userType: userType,
            isSignedUp: userData.isEoSignedUp || userData.isMpsSignedUp || false,
          };
        }
      }

      return {
        success: true,
        user: result.user,
        isNewUser: true,
        userType: null,
      };
    } catch (error) {
      if (error.code === 'auth/popup-closed-by-user') {
        return {
          success: false,
          message: "Sign-in cancelled. Please try again.",
        };
      }

      return {
        success: false,
        message: error.message || "Failed to sign in with Google. Please try again.",
      };
    }
  },

  async checkExistingUserByUid(uid, userType) {
    try {
      const userDoc = await getDoc(doc(db, "users", uid));

      if (!userDoc.exists()) {
        return { exists: false };
      }

      const userData = userDoc.data();
      const hasProfile = userType === "EO" ? userData.eoProfile : userData.mpsProfile;
      const signInMethod = userType === "EO" ? userData.eoSignUpMethod : userData.mpsSignUpMethod;
      const isSignedUp = userType === "EO"
        ? (userData.isEoSignedUp || userData.eoProfile)
        : (userData.isMpsSignedUp || userData.mpsProfile);

      return {
        exists: true,
        hasProfile,
        signInMethod,
        isSignedUp: isSignedUp || false,
      };
    } catch (error) {
      console.error("Error checking user by UID:", error);
      return { exists: false };
    }
  },

  async signInWithGoogle(userType) {
    try {
      provider.setCustomParameters({
        prompt: 'select_account'
      });

      const result = await signInWithPopup(auth, provider);
      const email = result.user.email;
      const uid = result.user.uid;

      // Check by UID instead of email to comply with Firestore rules
      const existingUser = await this.checkExistingUserByUid(uid, userType);

      if (existingUser.exists && existingUser.hasProfile) {
        // Update signedInAs for this session
        await setDoc(doc(db, "users", uid), { signedInAs: userType, updatedAt: new Date() }, { merge: true });
        return {
          success: true,
          user: result.user,
          isSignedUp: existingUser.isSignedUp,
          userType: userType,
        };
      }

      await this.updateUserProfile(uid, email, userType, "Google");
      return {
        success: true,
        user: result.user,
        isSignedUp: false,
        userType: userType,
      };
    } catch (error) {
      if (error.code === 'auth/popup-closed-by-user') {
        return {
          success: false,
          message: "Sign-in cancelled. Please try again.",
        };
      }

      return {
        success: false,
        message: error.message || "Failed to sign in with Google. Please try again.",
      };
    }
  },
  async updateSessionUserType(uid, sessionId, userType) {
    try {
      const sessionRef = ref(realtimeDb, `userSessions/${uid}/${sessionId}`);
      await set(sessionRef, {
        userType,
        lastActive: new Date().toISOString(),
      });
      return { success: true };
    } catch (error) {
      console.error("Error updating session user type:", error);
      return { success: false, error };
    }
  },
  async clearSession(uid, sessionId) {
    try {
      await remove(ref(realtimeDb, `userSessions/${uid}/${sessionId}`));
      return { success: true };
    } catch (error) {
      console.error("Error clearing session:", error);
      return { success: false, error };
    }
  },
  async cleanupOldSessions(uid) {
    try {
      const sessionsRef = ref(realtimeDb, `userSessions/${uid}`);
      const sessionsSnapshot = await get(sessionsRef);
      if (!sessionsSnapshot.exists()) return;

      const sessions = sessionsSnapshot.val();
      const now = new Date();
      const twentyFourHours = 24 * 60 * 60 * 1000;

      Object.entries(sessions).forEach(async ([sessionId, sessionData]) => {
        const lastActive = new Date(sessionData.lastActive);
        if (now - lastActive > twentyFourHours) {
          const oldSessionRef = ref(
            realtimeDb,
            `userSessions/${uid}/${sessionId}`
          );
          await set(oldSessionRef, null);
        }
      });
    } catch (error) {
      console.error("Error cleaning up old sessions:", error);
    }
  },
  async signUpWithGoogle(userType) {
    try {
      // Configure provider with prompt to always show account selection
      provider.setCustomParameters({
        prompt: 'select_account'
      });

      // Use popup instead of redirect
      const result = await signInWithPopup(auth, provider);
      const email = result.user.email;
      const uid = result.user.uid;

      // Check by UID instead of email to comply with Firestore rules
      const existingUser = await this.checkExistingUserByUid(uid, userType);

      if (existingUser.exists && existingUser.hasProfile) {
        // Update signedInAs for this session
        await setDoc(doc(db, "users", uid), { signedInAs: userType, updatedAt: new Date() }, { merge: true });
        return {
          success: true,
          user: result.user,
          isSignedUp: existingUser.isSignedUp,
          userType: userType,
        };
      }

      await this.updateUserProfile(uid, email, userType, "Google");
      return {
        success: true,
        user: result.user,
        isSignedUp: false,
        userType: userType,
      };
    } catch (error) {
      if (error.code === 'auth/popup-closed-by-user') {
        return {
          success: false,
          message: "Sign-up cancelled. Please try again.",
        };
      }

      return {
        success: false,
        message: error.message || "Failed to sign up with Google. Please try again.",
      };
    }
  },

  async handleRedirectResult() {
    // Popup-based auth doesn't need redirect result handling
    return { success: false, noRedirect: true };
  },

  async exchangeTokenForCustomToken(idToken) {
    try {
      const backendUrl = process.env.NODE_ENV === 'production' 
        ? '/api/auth/exchange-token'
        : 'http://localhost:3001/api/auth/exchange-token';
      
      const response = await fetch(backendUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ idToken }),
      });
      
      if (!response.ok) {
        const error = await response.json();
        return { success: false, message: error.error || 'Token exchange failed' };
      }
      
      const { customToken, uid } = await response.json();
      return { success: true, customToken, uid };
    } catch (error) {
      console.error('Token exchange error:', error);
      return { success: false, message: 'Failed to exchange token' };
    }
  },

  async signInWithToken(customToken) {
    try {
      const result = await signInWithCustomToken(auth, customToken);
      return { success: true, user: result.user };
    } catch (error) {
      console.error('Custom token sign-in error:', error);
      return { success: false, message: error.message || 'Failed to sign in' };
    }
  },

  async getIdToken() {
    try {
      const user = auth.currentUser;
      if (!user) {
        return { success: false, message: 'No user signed in' };
      }
      const idToken = await user.getIdToken(true);
      return { success: true, idToken };
    } catch (error) {
      console.error('Get ID token error:', error);
      return { success: false, message: error.message };
    }
  },

  async setUserSignedUp(uid, userType, isSignedUp) {
    try {
      if (!userType) {
        return {
          success: false,
          message: "User type is required",
        };
      }
      
      const userRef = doc(db, "users", uid);
      const updates = {
        updatedAt: new Date(),
        signedInAs: userType,
      };

      if (userType === "EO") {
        updates.isEoSignedUp = isSignedUp;
        updates.eoProfile = true;
      } else if (userType === "MPS") {
        updates.isMpsSignedUp = isSignedUp;
        updates.mpsProfile = true;
      }

      // Use setDoc with merge to create document if it doesn't exist
      await setDoc(userRef, updates, { merge: true });
      return {
        success: true,
      };
    } catch (error) {
      return {
        success: false,
        message: error.message,
      };
    }
  },

  /**
   * @deprecated This function queries by email which requires collection-level read access.
   * Use checkExistingUserByUid() after authenticating instead.
   */
  async checkExistingUser(email, userType) {
    const usersRef = collection(db, "users");
    const q = query(usersRef, where("email", "==", email));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return { exists: false };
    }

    const userData = querySnapshot.docs[0].data();
    const hasProfile =
      userType === "EO" ? userData.eoProfile : userData.mpsProfile;
    const signInMethod =
      userType === "EO" ? userData.eoSignUpMethod : userData.mpsSignUpMethod;

    // If user has a profile, they're signed up (fixes legacy accounts missing the flag)
    const isSignedUp = userType === "EO"
      ? (userData.isEoSignedUp || userData.eoProfile)
      : (userData.isMpsSignedUp || userData.mpsProfile);

    return {
      exists: true,
      hasProfile,
      signInMethod,
      isSignedUp: isSignedUp || false,
    };
  },

  async updateUserProfile(uid, email, userType, signInMethod, emailVerified = false) {
    const userRef = doc(db, "users", uid);
    const userDoc = await getDoc(userRef);
    const existingData = userDoc.exists() ? userDoc.data() : {};
    const isNewUser = !userDoc.exists();

    const updates = {
      uid,
      email,
      signedInAs: userType,
      emailVerified,
      createdAt: existingData.createdAt || new Date(),
      updatedAt: new Date(),
      ...existingData,
    };

    // For new users, initialize onboarding state to show welcome modal
    if (isNewUser || !existingData.onboarding) {
      updates.onboarding = {
        hasSeenWelcome: false,
        hasSeenDashboardTips: false,
        hasSeenEventsTips: false,
        hasSeenSettingsTips: false,
        hasSeenMarketplaceTips: false,
        hasSeenReceiptsTips: false,
        hasSeenCRMTips: false,
        hasSeenAnalyticsTips: false,
        completedSteps: [],
        dismissedTips: [],
        onboardingComplete: false,
      };
      // Set flag to show welcome modal on first sign-in
      updates.has_signed_in_first_time = true;
    }

    if (userType === "EO") {
      updates.eoProfile = true;
      updates.eoSignUpMethod = signInMethod;
      updates.isEoSignedUp = existingData.isEoSignedUp || false;

      // Generate referral code for new EO accounts
      if (!existingData.referralCode) {
        // Use a temporary business name if not available
        const tempBusinessName = email.split('@')[0];
        updates.referralCode = await generateReferralCode(tempBusinessName);
        updates.referralCount = 0;
        updates.referralRewards = [];
      }
    } else {
      updates.mpsProfile = true;
      updates.mpsSignUpMethod = signInMethod;
      updates.isMpsSignedUp = existingData.isMpsSignedUp || false;
    }

    await setDoc(userRef, updates, { merge: true });
    return updates;
  },

  async updateSignInMethod(uid, userType) {
    const userRef = doc(db, "users", uid);
    await updateDoc(userRef, {
      signedInAs: userType,
      updatedAt: new Date(),
    });
  },

  async updateReferralCode(uid, businessName) {
    try {
      const userRef = doc(db, "users", uid);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        throw new Error("User not found");
      }

      const userData = userDoc.data();
      
      // Only generate new code if one doesn't exist or business name changed
      if (!userData.referralCode || userData.businessName !== businessName) {
        const referralCode = await generateReferralCode(businessName);
        await updateDoc(userRef, {
          referralCode,
          businessName,
          updatedAt: new Date()
        });
        return { success: true, referralCode };
      }

      return { success: true, referralCode: userData.referralCode };
    } catch (error) {
      console.error("Error updating referral code:", error);
      return { success: false, error: error.message };
    }
  },

  async getReferralCode(uid) {
    try {
      const userRef = doc(db, "users", uid);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        throw new Error("User not found");
      }

      const userData = userDoc.data();
      return { 
        success: true, 
        referralCode: userData.referralCode,
        referralCount: userData.referralCount || 0,
        referralRewards: userData.referralRewards || []
      };
    } catch (error) {
      console.error("Error getting referral code:", error);
      return { success: false, error: error.message };
    }
  },

  async validateReferralCode(code) {
    try {
      if (!code) {
        return { success: false, message: "Please enter a referral code" };
      }

      // Note: This queries the users collection which requires special Firestore rules
      // to allow unauthenticated access to the referralCode field only.
      // If this fails due to permissions, we return a soft failure.
      const usersRef = collection(db, "users");
      const q = query(usersRef, where("referralCode", "==", code));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        return { success: false, message: "Invalid referral code" };
      }

      const referrerData = querySnapshot.docs[0].data();
      return {
        success: true,
        message: "Valid referral code",
        referrerId: querySnapshot.docs[0].id,
        referrerName: referrerData.businessName || referrerData.email?.split('@')[0] || "Referrer"
      };
    } catch (error) {
      console.error("Error validating referral code:", error);
      // Return a soft failure - don't block signup if validation fails
      return { success: false, message: "Could not validate code. You can still sign up." };
    }
  }
};
